import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  Wifi,
  WifiOff,
  RefreshCw,
  User,
  Hash,
  Zap,
  X,
  Clock,
  Shuffle,
  ArrowRightLeft,
  Ban,
  Settings,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Lock,
  Unlock,
  Users,
  Headphones,
  MicOff,
  VolumeX,
  Repeat,
  Bolt,
  Send,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DisconnectTool() {
  const [voiceMembers, setVoiceMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [guildRoles, setGuildRoles] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberPickerScope, setMemberPickerScope] = useState("voice"); // "voice" | "all"
  const [voiceChannels, setVoiceChannels] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [voiceLocked, setVoiceLocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedForLock, setSelectedForLock] = useState([]);
  const [lockChannels, setLockChannels] = useState([]); // empty = all channels
  const [actionCount, setActionCount] = useState(3);
  const [actionType, setActionType] = useState("disconnect");
  const [targetChannelId, setTargetChannelId] = useState("");
  const [excludedChannels, setExcludedChannels] = useState([]);
  const [showExcludeSettings, setShowExcludeSettings] = useState(false);
  const [showVoiceLock, setShowVoiceLock] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState("actions"); // "actions" | "voicelock" | "cone" | "boomerang" | "bulkmove"
  const [conedMembers, setConedMembers] = useState([]);
  const [selectedForCone, setSelectedForCone] = useState([]);
  const [coneDeafen, setConeDeafen] = useState(true);
  const [coneMute, setConeMute] = useState(false);
  const [boomerangs, setBoomerangs] = useState([]);
  const [boomerangMemberId, setBoomerangMemberId] = useState("");
  const [boomerangAnchorId, setBoomerangAnchorId] = useState("");
  const [bulkMoveTarget, setBulkMoveTarget] = useState("");
  const [bulkMoveSelected, setBulkMoveSelected] = useState([]);
  const [quickSheetOpen, setQuickSheetOpen] = useState(false);
  const [quickActionMember, setQuickActionMember] = useState(null);
  
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username");

  // Load excluded channels from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("controlTool_excludedChannels");
    if (saved) {
      try {
        setExcludedChannels(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse excluded channels:", e);
      }
    }
  }, []);

  // Save excluded channels to localStorage when changed
  useEffect(() => {
    localStorage.setItem("controlTool_excludedChannels", JSON.stringify(excludedChannels));
  }, [excludedChannels]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [voiceRes, channelsRes, pendingRes, lockedRes, allMembersRes, rolesRes, coneRes, boomRes] = await Promise.all([
        axios.get(`${API}/discord/voice-members`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/discord/voice-channels`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/discord/pending-disconnects`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/discord/voice-locked`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/discord/all-guild-members`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/discord/guild-roles`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/discord/cone-of-silence`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/discord/boomerang`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
      ]);
      setVoiceMembers(voiceRes.data || []);
      setVoiceChannels(channelsRes.data || []);
      setPendingActions(pendingRes.data || []);
      setVoiceLocked(lockedRes.data || []);
      setAllMembers(allMembersRes.data || []);
      setGuildRoles(rolesRes.data || []);
      setConedMembers(coneRes.data || []);
      setBoomerangs(boomRes.data || []);
      
      // Set default target channel if available (excluding excluded ones)
      const availableChannels = (channelsRes.data || []).filter(
        ch => !excludedChannels.includes(ch.id)
      );
      if (availableChannels.length > 0 && !targetChannelId) {
        setTargetChannelId(availableChannels[0].id);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied");
        navigate("/");
      } else {
        toast.error(error.response?.data?.detail || "Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, targetChannelId, excludedChannels]);

  // Access gate: National + manage_control_tool permission (granted via Permission Panel)
  const userChapter = localStorage.getItem("chapter") || "";
  const userPermissions = (() => {
    try { return JSON.parse(localStorage.getItem("permissions") || "{}"); } catch { return {}; }
  })();
  const isAuthorizedForControlTool =
    userChapter === "National" && userPermissions.manage_control_tool === true;

  useEffect(() => {
    // Check if user is authorized
    if (!isAuthorizedForControlTool) {
      toast.error("Access denied. This page is restricted.");
      navigate("/");
      return;
    }
    setAuthorized(true);
    fetchData();
    
    // Auto-refresh every 5 seconds
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [isAuthorizedForControlTool, navigate, fetchData]);

  // Get available channels (excluding excluded ones)
  const availableChannels = voiceChannels.filter(ch => !excludedChannels.includes(ch.id));

  const toggleExcludedChannel = (channelId) => {
    setExcludedChannels(prev => {
      if (prev.includes(channelId)) {
        return prev.filter(id => id !== channelId);
      } else {
        return [...prev, channelId];
      }
    });
    if (targetChannelId === channelId) {
      const remaining = voiceChannels.filter(ch => ch.id !== channelId && !excludedChannels.includes(ch.id));
      setTargetChannelId(remaining.length > 0 ? remaining[0].id : "");
    }
  };

  const toggleSelectForLock = (discordId) => {
    setSelectedForLock(prev => {
      if (prev.includes(discordId)) {
        return prev.filter(id => id !== discordId);
      } else {
        return [...prev, discordId];
      }
    });
  };

  const toggleLockChannel = (channelId) => {
    setLockChannels(prev => {
      if (prev.includes(channelId)) {
        return prev.filter(id => id !== channelId);
      } else {
        return [...prev, channelId];
      }
    });
  };

  const handleExecute = async () => {
    if (!selectedMember) {
      toast.error("Please select a member first");
      return;
    }

    if (actionType === "move_specific" && !targetChannelId) {
      toast.error("Please select a target channel");
      return;
    }

    if (actionType === "move_random" && availableChannels.length < 2) {
      toast.error("Need at least 2 available channels for random move");
      return;
    }

    try {
      setIsExecuting(true);
      setLastResult(null);
      
      const response = await axios.post(
        `${API}/discord/disconnect-member`,
        {
          discord_id: selectedMember.discord_id,
          count: actionCount,
          action: actionType,
          target_channel_id: actionType === "move_specific" ? targetChannelId : null,
          excluded_channel_ids: excludedChannels,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setLastResult(response.data);
      toast.success(response.data.message);
      setSelectedMember(null);
      fetchData();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute action");
      setLastResult({ success: false, message: error.response?.data?.detail || "Error" });
    } finally {
      setIsExecuting(false);
    }
  };

  const handleLockSelected = async () => {
    if (selectedForLock.length === 0) {
      toast.error("Please select members to lock");
      return;
    }

    try {
      setIsExecuting(true);
      const response = await axios.post(
        `${API}/discord/voice-lock`,
        { discord_ids: selectedForLock, channel_ids: lockChannels },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setSelectedForLock([]);
      setLockChannels([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to lock members");
    } finally {
      setIsExecuting(false);
    }
  };

  const handleUnlock = async (discordId) => {
    try {
      await axios.delete(`${API}/discord/voice-lock/${discordId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Member unlocked");
      fetchData();
    } catch (error) {
      toast.error("Failed to unlock member");
    }
  };

  const handleUnlockAll = async () => {
    try {
      await axios.delete(`${API}/discord/voice-lock-all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("All members unlocked");
      fetchData();
    } catch (error) {
      toast.error("Failed to unlock members");
    }
  };

  const toggleSelectForCone = (discordId) => {
    setSelectedForCone(prev =>
      prev.includes(discordId)
        ? prev.filter(id => id !== discordId)
        : [...prev, discordId]
    );
  };

  const handleApplyCone = async (action) => {
    if (action === "apply" && selectedForCone.length === 0) {
      toast.error("Pick at least one member");
      return;
    }
    if (action === "apply" && !coneDeafen && !coneMute) {
      toast.error("Pick at least one effect (deafen or mute)");
      return;
    }
    try {
      setIsExecuting(true);
      const ids = action === "apply"
        ? selectedForCone
        : conedMembers.map(m => m.discord_id);
      if (ids.length === 0) {
        toast.info("No members to lift");
        return;
      }
      const response = await axios.post(
        `${API}/discord/cone-of-silence`,
        { discord_ids: ids, deafen: coneDeafen, mute: coneMute, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { applied_count, skipped_count, message } = response.data;
      if (applied_count > 0) {
        toast.success(message);
      } else if (skipped_count > 0) {
        toast.warning(message);
      } else {
        toast.info(message);
      }
      if (action === "apply") setSelectedForCone([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} cone`);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleCancelPending = async (discordId) => {
    try {
      await axios.delete(`${API}/discord/pending-disconnects/${discordId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Pending actions cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel pending actions");
    }
  };

  const handleSetBoomerang = async () => {
    if (!boomerangMemberId) {
      toast.error("Pick a member to boomerang");
      return;
    }
    try {
      setIsExecuting(true);
      const body = { discord_id: boomerangMemberId };
      if (boomerangAnchorId) body.anchor_channel_id = boomerangAnchorId;
      const response = await axios.post(
        `${API}/discord/boomerang`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setBoomerangMemberId("");
      setBoomerangAnchorId("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set boomerang");
    } finally {
      setIsExecuting(false);
    }
  };

  const handleClearBoomerang = async (discordId) => {
    try {
      await axios.delete(`${API}/discord/boomerang/${discordId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Boomerang cleared");
      fetchData();
    } catch (error) {
      toast.error("Failed to clear boomerang");
    }
  };

  const handleClearAllBoomerangs = async () => {
    try {
      const response = await axios.delete(`${API}/discord/boomerang`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to clear boomerangs");
    }
  };

  // Quick Actions (mobile floating bar) — single-tap immediate actions
  const toggleBulkMoveMember = (discordId) => {
    setBulkMoveSelected((prev) =>
      prev.includes(discordId) ? prev.filter((id) => id !== discordId) : [...prev, discordId]
    );
  };

  const handleBulkMove = async () => {
    if (bulkMoveSelected.length === 0) {
      toast.error("Pick at least one member");
      return;
    }
    if (!bulkMoveTarget) {
      toast.error("Pick a target channel");
      return;
    }
    try {
      setIsExecuting(true);
      const response = await axios.post(
        `${API}/discord/bulk-move`,
        { discord_ids: bulkMoveSelected, target_channel_id: bulkMoveTarget },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { moved_count, skipped_count, message } = response.data;
      if (moved_count > 0) toast.success(message);
      else if (skipped_count > 0) toast.warning(message);
      else toast.info(message);
      setBulkMoveSelected([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk move failed");
    } finally {
      setIsExecuting(false);
    }
  };

  // Quick Actions (mobile floating bar) — single-tap immediate actions
  const runQuickAction = async (member, action) => {
    if (!member) return;
    try {
      setIsExecuting(true);
      if (action === "disconnect") {
        await axios.post(
          `${API}/discord/disconnect-member`,
          { discord_id: member.discord_id, count: 1, action: "disconnect" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Disconnected ${member.display_name}`);
      } else if (action === "random") {
        await axios.post(
          `${API}/discord/disconnect-member`,
          { discord_id: member.discord_id, count: 1, action: "move_random", excluded_channel_ids: excludedChannels },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Random-moved ${member.display_name}`);
      } else if (action === "deafen") {
        await axios.post(
          `${API}/discord/cone-of-silence`,
          { discord_ids: [member.discord_id], deafen: true, mute: false, action: "apply" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Deafened ${member.display_name}`);
      } else if (action === "lock") {
        await axios.post(
          `${API}/discord/voice-lock`,
          { discord_ids: [member.discord_id], channel_ids: [] },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Locked ${member.display_name}`);
      } else if (action === "boomerang") {
        await axios.post(
          `${API}/discord/boomerang`,
          { discord_id: member.discord_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Boomerang set for ${member.display_name}`);
      }
      setQuickActionMember(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Quick action failed");
    } finally {
      setIsExecuting(false);
    }
  };

  const getPendingInfo = (discordId) => pendingActions.find(p => p.discord_id === discordId);
  const isVoiceLocked = (discordId) => voiceLocked.some(l => l.discord_id === discordId);

  const getActionLabel = (action, targetChannel) => {
    switch (action) {
      case "move_specific": return `Move to ${targetChannel || "channel"}`;
      case "move_random": return "Random move";
      default: return "Disconnect";
    }
  };

  if (!authorized) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-3 sm:p-4 md:p-8">
      <div className="max-w-2xl md:max-w-4xl xl:max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="text-slate-400 hover:text-white flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center gap-2 sm:gap-3">
              <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 flex-shrink-0" />
              <span className="truncate">Control Tool</span>
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-1 flex flex-wrap items-center gap-2">
              <span>Manage voice actions and locks</span>
              <span
                className="text-[10px] uppercase tracking-wide bg-slate-700/80 text-slate-200 px-2 py-0.5 rounded flex items-center gap-1 flex-shrink-0"
                title="No Discord audit-log entries, no in-app activity log"
                data-testid="silent-mode-badge"
              >
                <span>🤫</span> Silent Mode
              </span>
            </p>
          </div>
        </div>

        {/* Tab Selector — 2-cols on mobile, 3-cols on tablet, 5-cols on laptop+ */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-4 sm:mb-6">
          <button
            onClick={() => setActiveTab("actions")}
            data-testid="tab-actions"
            className={`py-2.5 md:py-3 px-2 md:px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 md:gap-2 text-sm md:text-base ${
              activeTab === "actions"
                ? 'bg-purple-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Zap className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Actions</span>
          </button>
          <button
            onClick={() => setActiveTab("voicelock")}
            data-testid="tab-voicelock"
            className={`py-2.5 md:py-3 px-2 md:px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 md:gap-2 text-sm md:text-base ${
              activeTab === "voicelock"
                ? 'bg-red-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Lock className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              <span className="hidden sm:inline">Voice </span>Lock
              <span className="ml-1">({voiceLocked.length})</span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab("cone")}
            data-testid="tab-cone"
            className={`py-2.5 md:py-3 px-2 md:px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 md:gap-2 text-sm md:text-base ${
              activeTab === "cone"
                ? 'bg-amber-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Headphones className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              Cone<span className="hidden sm:inline"> of Silence</span>
              <span className="ml-1">({conedMembers.length})</span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab("boomerang")}
            data-testid="tab-boomerang"
            className={`py-2.5 md:py-3 px-2 md:px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 md:gap-2 text-sm md:text-base ${
              activeTab === "boomerang"
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Repeat className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Boomerang ({boomerangs.length})</span>
          </button>
          <button
            onClick={() => setActiveTab("bulkmove")}
            data-testid="tab-bulkmove"
            className={`py-2.5 md:py-3 px-2 md:px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-1.5 md:gap-2 text-sm md:text-base ${
              activeTab === "bulkmove"
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Send className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              <span className="hidden sm:inline">Bulk </span>Move
            </span>
          </button>
        </div>

        {/* Pending Actions Banner */}
        {pendingActions.length > 0 && activeTab === "actions" && (
          <div className="bg-orange-900/30 border border-orange-700 rounded-xl p-4 mb-6">
            <h3 className="text-orange-300 font-semibold flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5" />
              Active Auto-Actions ({pendingActions.length})
            </h3>
            <div className="space-y-2">
              {pendingActions.map((pending) => (
                <div key={pending.discord_id} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                  <div>
                    <span className="text-white font-medium">{pending.display_name}</span>
                    <span className="text-orange-400 ml-2">
                      ({pending.remaining_count}x {getActionLabel(pending.action, pending.target_channel)})
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleCancelPending(pending.discord_id)} className="text-red-400 hover:text-red-300 hover:bg-red-900/30">
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === "actions" && (
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 sm:p-6">
            {/* Voice Members Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-green-400" />
                  Members in Voice ({voiceMembers.length})
                </h2>
                <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>

              {loading && voiceMembers.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" /> Loading...
                </div>
              ) : voiceMembers.length === 0 ? (
                <div className="text-center py-8 text-slate-400 bg-slate-900/50 rounded-lg border border-slate-700">
                  <WifiOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No members currently in voice channels</p>
                </div>
              ) : (
                <div className="grid gap-2 max-h-48 overflow-y-auto">
                  {voiceMembers.map((member) => {
                    const pendingInfo = getPendingInfo(member.discord_id);
                    const locked = isVoiceLocked(member.discord_id);
                    return (
                      <button
                        key={member.discord_id}
                        onClick={() => setSelectedMember(member)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                          selectedMember?.discord_id === member.discord_id
                            ? 'bg-purple-600/30 border-purple-500 ring-2 ring-purple-500/50'
                            : locked
                            ? 'bg-red-900/20 border-red-700'
                            : pendingInfo
                            ? 'bg-orange-900/20 border-orange-700'
                            : 'bg-slate-900/50 border-slate-700 hover:bg-slate-800'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          locked ? 'bg-gradient-to-br from-red-600 to-red-800' :
                          pendingInfo ? 'bg-gradient-to-br from-orange-500 to-red-500' :
                          'bg-gradient-to-br from-purple-500 to-pink-500'
                        }`}>
                          {locked ? <Lock className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">
                            {member.display_name}
                            {locked && <span className="ml-2 text-xs bg-red-600 text-white px-2 py-0.5 rounded-full">LOCKED</span>}
                            {pendingInfo && <span className="ml-2 text-xs bg-orange-600 text-white px-2 py-0.5 rounded-full">{pendingInfo.remaining_count}x pending</span>}
                          </p>
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <Hash className="w-3 h-3" /> {member.channel_name}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Action Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-3">Action Type</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { type: "disconnect", icon: WifiOff, label: "Disconnect", color: "red" },
                  { type: "move_specific", icon: ArrowRightLeft, label: "Move to Room", color: "blue" },
                  { type: "move_random", icon: Shuffle, label: "Random Room", color: "purple" },
                  { type: "bounce", icon: RotateCcw, label: "Bounce", color: "green" },
                ].map(({ type, icon: Icon, label, color }) => (
                  <button
                    key={type}
                    onClick={() => setActionType(type)}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      actionType === type
                        ? `bg-${color}-600/30 border-${color}-500 text-${color}-300`
                        : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:bg-slate-800'
                    }`}
                    style={actionType === type ? {
                      backgroundColor: `rgba(var(--${color}-600), 0.3)`,
                      borderColor: color === "red" ? "#ef4444" : color === "blue" ? "#3b82f6" : color === "purple" ? "#a855f7" : "#22c55e",
                      color: color === "red" ? "#fca5a5" : color === "blue" ? "#93c5fd" : color === "purple" ? "#d8b4fe" : "#86efac"
                    } : {}}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs">{label}</span>
                  </button>
                ))}
              </div>
              {actionType === "bounce" && (
                <p className="text-xs text-green-400 mt-2">Moves through random channels then returns to starting room</p>
              )}
            </div>

            {/* Target Channel Selection */}
            {actionType === "move_specific" && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-3">Target Voice Channel</label>
                {availableChannels.length === 0 ? (
                  <p className="text-red-400 text-sm">No available channels (all excluded)</p>
                ) : (
                  <select
                    value={targetChannelId}
                    onChange={(e) => setTargetChannelId(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-3"
                  >
                    {availableChannels.map((channel) => (
                      <option key={channel.id} value={channel.id}>{channel.name} ({channel.member_count} members)</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Excluded Channels Settings */}
            <div className="mb-6">
              <button onClick={() => setShowExcludeSettings(!showExcludeSettings)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300">
                <Settings className="w-4 h-4" /> Excluded Rooms ({excludedChannels.length})
                {showExcludeSettings ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showExcludeSettings && (
                <div className="mt-3 p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <p className="text-xs text-slate-500 mb-3">Select rooms to exclude from move targets</p>
                  <div className="grid gap-2 max-h-40 overflow-y-auto">
                    {voiceChannels.map((channel) => (
                      <button
                        key={channel.id}
                        onClick={() => toggleExcludedChannel(channel.id)}
                        className={`flex items-center gap-3 p-2 rounded-lg border text-left text-sm ${
                          excludedChannels.includes(channel.id)
                            ? 'bg-red-900/30 border-red-700 text-red-300'
                            : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded flex items-center justify-center ${excludedChannels.includes(channel.id) ? 'bg-red-600' : 'bg-slate-700'}`}>
                          {excludedChannels.includes(channel.id) && <Ban className="w-3 h-3 text-white" />}
                        </div>
                        <Hash className="w-4 h-4 opacity-50" />
                        <span className="flex-1">{channel.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Count */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                {actionType === "bounce" ? "Number of Random Rooms to Visit" : "Number of Times"}
              </label>
              <div className="flex gap-2 flex-wrap">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <button
                    key={num}
                    onClick={() => setActionCount(num)}
                    className={`w-11 h-11 rounded-lg font-bold text-lg transition-all ${
                      actionCount === num
                        ? actionType === "disconnect" ? 'bg-red-600 text-white ring-2 ring-red-400'
                        : actionType === "move_random" ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                        : actionType === "bounce" ? 'bg-green-600 text-white ring-2 ring-green-400'
                        : 'bg-blue-600 text-white ring-2 ring-blue-400'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Execute Button */}
            <Button
              onClick={handleExecute}
              disabled={!selectedMember || isExecuting}
              className={`w-full h-12 sm:h-14 text-base sm:text-lg font-bold ${
                actionType === "disconnect" ? 'bg-gradient-to-r from-red-600 to-orange-600'
                : actionType === "move_random" ? 'bg-gradient-to-r from-purple-600 to-pink-600'
                : actionType === "bounce" ? 'bg-gradient-to-r from-green-600 to-teal-600'
                : 'bg-gradient-to-r from-blue-600 to-cyan-600'
              }`}
            >
              {isExecuting ? <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Processing...</> : (
                <>
                  {actionType === "disconnect" && <><WifiOff className="w-5 h-5 mr-2" /> Disconnect {selectedMember?.display_name || "Member"} {actionCount}x</>}
                  {actionType === "move_specific" && <><ArrowRightLeft className="w-5 h-5 mr-2" /> Move {selectedMember?.display_name || "Member"} {actionCount}x</>}
                  {actionType === "move_random" && <><Shuffle className="w-5 h-5 mr-2" /> Random Move {selectedMember?.display_name || "Member"} {actionCount}x</>}
                  {actionType === "bounce" && <><RotateCcw className="w-5 h-5 mr-2" /> Bounce {selectedMember?.display_name || "Member"} through {actionCount} rooms</>}
                </>
              )}
            </Button>

            {lastResult && (
              <div className={`mt-4 p-4 rounded-lg border ${lastResult.success ? 'bg-green-900/30 border-green-700 text-green-300' : 'bg-red-900/30 border-red-700 text-red-300'}`}>
                <p className="font-medium">{lastResult.success ? '✅' : '❌'} {lastResult.message}</p>
              </div>
            )}
          </div>
        )}

        {/* Voice Lock Tab */}
        {activeTab === "voicelock" && (
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 sm:p-6">
            {/* Currently Locked Members */}
            {voiceLocked.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Lock className="w-5 h-5 text-red-400" />
                    Currently Locked ({voiceLocked.length})
                  </h2>
                  <Button variant="outline" size="sm" onClick={handleUnlockAll} className="border-green-600 text-green-400 hover:bg-green-900/30">
                    <Unlock className="w-4 h-4 mr-2" /> Unlock All
                  </Button>
                </div>
                <div className="space-y-2">
                  {voiceLocked.map((locked) => (
                    <div key={locked.discord_id} className="flex items-start justify-between bg-red-900/20 border border-red-800 rounded-lg p-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                          <Lock className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium">{locked.display_name}</p>
                          <p className="text-xs text-red-400">Locked by {locked.locked_by}</p>
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {locked.locks_all_channels ? (
                              <span className="text-[10px] uppercase tracking-wide bg-red-700/60 text-red-100 px-2 py-0.5 rounded">
                                All voice channels
                              </span>
                            ) : (
                              (locked.channel_names || []).map((name, idx) => (
                                <span
                                  key={idx}
                                  className="text-[10px] bg-slate-700/80 text-slate-200 px-2 py-0.5 rounded flex items-center gap-1"
                                >
                                  <Hash className="w-2.5 h-2.5" /> {name}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleUnlock(locked.discord_id)} className="text-green-400 hover:text-green-300 hover:bg-green-900/30 flex-shrink-0">
                        <Unlock className="w-4 h-4 mr-1" /> Unlock
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Select Members to Lock */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-slate-400" />
                  Select Members to Lock
                </h2>
                <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="border-slate-600 text-slate-300">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>

              {/* Bulk select by role */}
              {guildRoles.length > 0 && (
                <div className="mb-3 p-3 bg-slate-900/50 border border-slate-700 rounded-lg" data-testid="bulk-role-select">
                  <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wide">
                    Bulk Select by Role
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      value=""
                      onChange={(e) => {
                        const roleId = e.target.value;
                        if (!roleId) return;
                        const role = guildRoles.find(r => r.id === roleId);
                        const roleMembers = allMembers.filter(m => (m.role_ids || []).includes(roleId));
                        const ids = roleMembers
                          .map(m => m.discord_id)
                          .filter(id => !isVoiceLocked(id));
                        if (ids.length === 0) {
                          toast.info(`No selectable members in ${role?.name}`);
                          e.target.value = "";
                          return;
                        }
                        setSelectedForLock(prev => Array.from(new Set([...prev, ...ids])));
                        setMemberPickerScope("all");
                        toast.success(`Added ${ids.length} member${ids.length !== 1 ? 's' : ''} from ${role?.name}`);
                        e.target.value = "";
                      }}
                      data-testid="bulk-role-dropdown"
                      className="flex-1 min-w-0 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:border-purple-500"
                    >
                      <option value="">Add all members from a role...</option>
                      {guildRoles.map(role => (
                        <option key={role.id} value={role.id} style={role.color ? { color: role.color } : {}}>
                          {role.name} ({role.member_count})
                        </option>
                      ))}
                    </select>
                    {selectedForLock.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedForLock([])}
                        data-testid="clear-selection-btn"
                        className="border-slate-600 text-slate-300 w-full sm:w-auto flex-shrink-0"
                      >
                        Clear ({selectedForLock.length})
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5">
                    Picks every member with the selected role. Already-locked members are skipped.
                  </p>
                </div>
              )}

              {/* Scope toggle: voice vs all members */}
              <div className="flex gap-2 mb-3" data-testid="lock-scope-toggle">
                <button
                  type="button"
                  onClick={() => setMemberPickerScope("voice")}
                  data-testid="lock-scope-voice-btn"
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    memberPickerScope === "voice"
                      ? 'bg-purple-600/30 border-purple-500 text-white'
                      : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:text-white'
                  }`}
                >
                  In Voice ({voiceMembers.length})
                </button>
                <button
                  type="button"
                  onClick={() => setMemberPickerScope("all")}
                  data-testid="lock-scope-all-btn"
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    memberPickerScope === "all"
                      ? 'bg-purple-600/30 border-purple-500 text-white'
                      : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:text-white'
                  }`}
                >
                  All Members ({allMembers.length})
                </button>
              </div>

              {/* Search box (visible in both scopes; required for All) */}
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder={memberPickerScope === "all" ? "Search by name or username..." : "Filter members..."}
                data-testid="lock-member-search"
                className="w-full mb-3 px-3 py-2 bg-slate-900/70 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
              />

              {(() => {
                const source = memberPickerScope === "all" ? allMembers : voiceMembers;
                const search = memberSearch.trim().toLowerCase();
                const filtered = source
                  .filter(m => !isVoiceLocked(m.discord_id))
                  .filter(m => {
                    if (!search) return true;
                    return (
                      (m.display_name || "").toLowerCase().includes(search) ||
                      (m.username || "").toLowerCase().includes(search)
                    );
                  });

                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-8 text-slate-400 bg-slate-900/50 rounded-lg border border-slate-700">
                      <WifiOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>
                        {memberPickerScope === "voice"
                          ? "No members currently in voice channels"
                          : search
                            ? `No members match "${memberSearch}"`
                            : "No members found"}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="grid gap-2 max-h-64 sm:max-h-80 overflow-y-auto" data-testid="lock-member-list">
                    {filtered.map((member) => (
                      <button
                        key={member.discord_id}
                        onClick={() => toggleSelectForLock(member.discord_id)}
                        data-testid={`lock-member-${member.discord_id}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                          selectedForLock.includes(member.discord_id)
                            ? 'bg-red-600/30 border-red-500 ring-2 ring-red-500/50'
                            : 'bg-slate-900/50 border-slate-700 hover:bg-slate-800'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedForLock.includes(member.discord_id) ? 'bg-red-600 border-red-600' : 'border-slate-600'
                        }`}>
                          {selectedForLock.includes(member.discord_id) && <Lock className="w-4 h-4 text-white" />}
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">{member.display_name}</p>
                          <p className="text-xs text-slate-400 flex items-center gap-1 truncate">
                            {member.in_voice || member.channel_name ? (
                              <>
                                <Hash className="w-3 h-3 flex-shrink-0" /> {member.channel_name}
                              </>
                            ) : (
                              <span className="text-slate-500 flex items-center gap-1">
                                <WifiOff className="w-3 h-3" /> Not in voice
                              </span>
                            )}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Lock Channel Selection */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-slate-300 flex items-center gap-2">
                  <Hash className="w-4 h-4 text-slate-400" />
                  Lock From Which Channels?
                </label>
                {lockChannels.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setLockChannels([])}
                    className="text-xs text-slate-400 hover:text-slate-200 underline"
                    data-testid="lock-channels-clear-btn"
                  >
                    Clear (lock all)
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Select specific voice channels to lock the member out of. Leave empty to lock from <span className="text-red-300 font-medium">ALL</span> voice channels.
              </p>
              {voiceChannels.length === 0 ? (
                <div className="text-center py-4 text-slate-500 bg-slate-900/50 rounded-lg border border-slate-700 text-sm">
                  Loading channels...
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 sm:max-h-56 overflow-y-auto" data-testid="lock-channels-list">
                  {voiceChannels.map((channel) => {
                    const checked = lockChannels.includes(channel.id);
                    return (
                      <button
                        key={channel.id}
                        type="button"
                        onClick={() => toggleLockChannel(channel.id)}
                        data-testid={`lock-channel-${channel.id}`}
                        className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-left text-sm ${
                          checked
                            ? 'bg-red-600/30 border-red-500 ring-1 ring-red-500/50'
                            : 'bg-slate-900/50 border-slate-700 hover:bg-slate-800'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          checked ? 'bg-red-600 border-red-600' : 'border-slate-600'
                        }`}>
                          {checked && <Lock className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <Hash className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <span className="text-white truncate">{channel.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="mt-2 text-xs">
                {lockChannels.length === 0 ? (
                  <span className="text-red-300">⚠ Will lock from ALL voice channels</span>
                ) : (
                  <span className="text-slate-400">Will lock from {lockChannels.length} channel{lockChannels.length !== 1 ? 's' : ''}</span>
                )}
              </div>
            </div>

            {/* Lock Button */}
            <Button
              onClick={handleLockSelected}
              disabled={selectedForLock.length === 0 || isExecuting}
              data-testid="lock-selected-btn"
              className="w-full h-12 sm:h-14 text-base sm:text-lg font-bold bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900"
            >
              {isExecuting ? (
                <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Locking...</>
              ) : (
                <><Lock className="w-5 h-5 mr-2" /> Lock {selectedForLock.length} Member{selectedForLock.length !== 1 ? 's' : ''} {lockChannels.length > 0 ? `from ${lockChannels.length} Channel${lockChannels.length !== 1 ? 's' : ''}` : 'from All Voice'}</>
              )}
            </Button>

            <p className="text-xs text-slate-500 mt-4 text-center">
              Locked members will be immediately kicked if currently in a locked channel and blocked from rejoining selected channels until unlocked.
            </p>
          </div>
        )}

        {/* Cone of Silence Tab */}
        {activeTab === "cone" && (
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 sm:p-6">
            {/* Currently under cone */}
            {conedMembers.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Headphones className="w-5 h-5 text-amber-400" />
                    Currently Under Cone ({conedMembers.length})
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleApplyCone("lift")}
                    disabled={isExecuting}
                    data-testid="lift-all-cone-btn"
                    className="border-green-600 text-green-400 hover:bg-green-900/30"
                  >
                    <Unlock className="w-4 h-4 mr-2" /> Lift All
                  </Button>
                </div>
                <div className="space-y-2">
                  {conedMembers.map((m) => (
                    <div
                      key={m.discord_id}
                      className="flex items-center justify-between bg-amber-900/20 border border-amber-800 rounded-lg p-3"
                      data-testid={`coned-${m.discord_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-amber-600 flex items-center justify-center flex-shrink-0">
                          <Headphones className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">{m.display_name}</p>
                          <p className="text-xs text-amber-300 flex items-center gap-1">
                            <Hash className="w-3 h-3" /> {m.channel_name}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {m.deafened && (
                              <span className="text-[10px] uppercase bg-amber-700/60 text-amber-100 px-2 py-0.5 rounded flex items-center gap-1">
                                <VolumeX className="w-2.5 h-2.5" /> Deafened
                              </span>
                            )}
                            {m.muted && (
                              <span className="text-[10px] uppercase bg-amber-700/60 text-amber-100 px-2 py-0.5 rounded flex items-center gap-1">
                                <MicOff className="w-2.5 h-2.5" /> Muted
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            setIsExecuting(true);
                            await axios.post(
                              `${API}/discord/cone-of-silence`,
                              { discord_ids: [m.discord_id], deafen: true, mute: true, action: "lift" },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            toast.success(`Lifted cone on ${m.display_name}`);
                            fetchData();
                          } catch (err) {
                            toast.error("Failed to lift cone");
                          } finally {
                            setIsExecuting(false);
                          }
                        }}
                        data-testid={`lift-cone-${m.discord_id}`}
                        className="text-green-400 hover:text-green-300 hover:bg-green-900/30 flex-shrink-0"
                      >
                        <Unlock className="w-4 h-4 mr-1" /> Lift
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Effect toggles */}
            <div className="mb-5 p-4 bg-slate-900/50 border border-slate-700 rounded-lg">
              <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wide">
                Cone Effects
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConeDeafen(!coneDeafen)}
                  data-testid="cone-deafen-toggle"
                  className={`flex-1 p-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                    coneDeafen
                      ? 'bg-amber-600/30 border-amber-500 text-white ring-1 ring-amber-500/50'
                      : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white'
                  }`}
                >
                  <VolumeX className="w-4 h-4" />
                  Server-Deafen
                </button>
                <button
                  type="button"
                  onClick={() => setConeMute(!coneMute)}
                  data-testid="cone-mute-toggle"
                  className={`flex-1 p-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                    coneMute
                      ? 'bg-amber-600/30 border-amber-500 text-white ring-1 ring-amber-500/50'
                      : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white'
                  }`}
                >
                  <MicOff className="w-4 h-4" />
                  Server-Mute
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                <span className="text-amber-300">Deafen</span> stops them from hearing voice chat.{' '}
                <span className="text-amber-300">Mute</span> stops them from speaking.
              </p>
            </div>

            {/* Member picker (in-voice members only) */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-400" />
                  Pick Members in Voice ({voiceMembers.filter(m => !conedMembers.some(c => c.discord_id === m.discord_id)).length})
                </h2>
                <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="border-slate-600 text-slate-300">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>

              {voiceMembers.length === 0 ? (
                <div className="text-center py-8 text-slate-400 bg-slate-900/50 rounded-lg border border-slate-700">
                  <WifiOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No members currently in voice channels</p>
                </div>
              ) : (
                <div className="grid gap-2 max-h-64 sm:max-h-80 overflow-y-auto" data-testid="cone-member-list">
                  {voiceMembers
                    .filter(m => !conedMembers.some(c => c.discord_id === m.discord_id))
                    .map((member) => {
                      const checked = selectedForCone.includes(member.discord_id);
                      return (
                        <button
                          key={member.discord_id}
                          onClick={() => toggleSelectForCone(member.discord_id)}
                          data-testid={`cone-pick-${member.discord_id}`}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                            checked
                              ? 'bg-amber-600/30 border-amber-500 ring-2 ring-amber-500/50'
                              : 'bg-slate-900/50 border-slate-700 hover:bg-slate-800'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            checked ? 'bg-amber-600 border-amber-600' : 'border-slate-600'
                          }`}>
                            {checked && <Headphones className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{member.display_name}</p>
                            <p className="text-xs text-slate-400 flex items-center gap-1 truncate">
                              <Hash className="w-3 h-3 flex-shrink-0" /> {member.channel_name}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Apply button */}
            <Button
              onClick={() => handleApplyCone("apply")}
              disabled={selectedForCone.length === 0 || isExecuting || (!coneDeafen && !coneMute)}
              data-testid="apply-cone-btn"
              className="w-full h-12 sm:h-14 text-base sm:text-lg font-bold bg-gradient-to-r from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900"
            >
              {isExecuting ? (
                <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Working...</>
              ) : (
                <><Headphones className="w-5 h-5 mr-2" /> Apply Cone to {selectedForCone.length} Member{selectedForCone.length !== 1 ? 's' : ''}</>
              )}
            </Button>

            <p className="text-xs text-slate-500 mt-4 text-center">
              Server-deafen/mute is applied via the Discord bot. Members will see the indicator next to their name. Lift to remove.
            </p>
          </div>
        )}

        {/* Boomerang Tab */}
        {activeTab === "boomerang" && (
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 sm:p-6">
            {/* Currently boomeranged */}
            {boomerangs.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Repeat className="w-5 h-5 text-cyan-400" />
                    Active Boomerangs ({boomerangs.length})
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAllBoomerangs}
                    data-testid="clear-all-boomerangs-btn"
                    className="border-green-600 text-green-400 hover:bg-green-900/30"
                  >
                    <X className="w-4 h-4 mr-2" /> Clear All
                  </Button>
                </div>
                <div className="space-y-2">
                  {boomerangs.map((b) => (
                    <div
                      key={b.discord_id}
                      data-testid={`boomerang-${b.discord_id}`}
                      className="flex items-center justify-between bg-cyan-900/20 border border-cyan-800 rounded-lg p-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-cyan-600 flex items-center justify-center flex-shrink-0">
                          <Repeat className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">{b.display_name}</p>
                          <p className="text-xs text-cyan-300 flex items-center gap-1 truncate">
                            <Hash className="w-3 h-3 flex-shrink-0" />
                            Anchored to {b.anchor_channel_name}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            Set by {b.set_by}
                            {b.current_channel_name && b.current_channel_name !== b.anchor_channel_name && (
                              <span className="text-amber-400 ml-2">⚠ Currently in {b.current_channel_name}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleClearBoomerang(b.discord_id)}
                        data-testid={`clear-boomerang-${b.discord_id}`}
                        className="text-green-400 hover:text-green-300 hover:bg-green-900/30 flex-shrink-0"
                      >
                        <X className="w-4 h-4 mr-1" /> Clear
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Member picker */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                Pick Member to Boomerang
              </label>
              {voiceMembers.length === 0 ? (
                <div className="text-center py-8 text-slate-400 bg-slate-900/50 rounded-lg border border-slate-700">
                  <WifiOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No members currently in voice</p>
                  <p className="text-xs mt-1">Boomerang requires the member to be in voice (anchor defaults to their current channel).</p>
                </div>
              ) : (
                <div className="grid gap-2 max-h-56 sm:max-h-72 overflow-y-auto" data-testid="boomerang-member-list">
                  {voiceMembers
                    .filter(m => !boomerangs.some(b => b.discord_id === m.discord_id))
                    .map((member) => {
                      const checked = boomerangMemberId === member.discord_id;
                      return (
                        <button
                          key={member.discord_id}
                          onClick={() => {
                            setBoomerangMemberId(member.discord_id);
                            setBoomerangAnchorId(""); // default to current channel
                          }}
                          data-testid={`boomerang-pick-${member.discord_id}`}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                            checked
                              ? 'bg-cyan-600/30 border-cyan-500 ring-2 ring-cyan-500/50'
                              : 'bg-slate-900/50 border-slate-700 hover:bg-slate-800'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            checked ? 'bg-cyan-600 border-cyan-600' : 'border-slate-600'
                          }`}>
                            {checked && <Repeat className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{member.display_name}</p>
                            <p className="text-xs text-slate-400 flex items-center gap-1 truncate">
                              <Hash className="w-3 h-3 flex-shrink-0" /> {member.channel_name}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Optional anchor override */}
            {boomerangMemberId && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Anchor Channel <span className="text-slate-500 text-xs">(leave default to use their current channel)</span>
                </label>
                <select
                  value={boomerangAnchorId}
                  onChange={(e) => setBoomerangAnchorId(e.target.value)}
                  data-testid="boomerang-anchor-select"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500"
                >
                  <option value="">Use member's current channel</option>
                  {voiceChannels.map((ch) => (
                    <option key={ch.id} value={ch.id}>#{ch.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Set button */}
            <Button
              onClick={handleSetBoomerang}
              disabled={!boomerangMemberId || isExecuting}
              data-testid="set-boomerang-btn"
              className="w-full h-12 sm:h-14 text-base sm:text-lg font-bold bg-gradient-to-r from-cyan-600 to-cyan-800 hover:from-cyan-700 hover:to-cyan-900"
            >
              {isExecuting ? (
                <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Setting...</>
              ) : (
                <><Repeat className="w-5 h-5 mr-2" /> Set Boomerang</>
              )}
            </Button>

            <p className="text-xs text-slate-500 mt-4 text-center">
              The bot will snap the member back to their anchor channel any time they switch rooms — until you clear it.
            </p>
          </div>
        )}

        {/* Bulk Move Tab */}
        {activeTab === "bulkmove" && (
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-4 sm:p-6">
            {/* Target channel */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Hash className="w-4 h-4 text-slate-400" />
                Move To Which Channel?
              </label>
              <select
                value={bulkMoveTarget}
                onChange={(e) => setBulkMoveTarget(e.target.value)}
                data-testid="bulkmove-target-select"
                className="w-full px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500"
              >
                <option value="">Select a target voice channel...</option>
                {voiceChannels.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    #{ch.name} ({ch.member_count || 0})
                  </option>
                ))}
              </select>
            </div>

            {/* Member picker */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-400" />
                  Pick Members in Voice ({voiceMembers.filter(m => String(m.channel_id) !== String(bulkMoveTarget)).length})
                </h2>
                <div className="flex gap-2">
                  {voiceMembers.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const all = voiceMembers
                          .filter(m => String(m.channel_id) !== String(bulkMoveTarget))
                          .map(m => m.discord_id);
                        setBulkMoveSelected(all);
                      }}
                      data-testid="bulkmove-select-all-btn"
                      className="border-slate-600 text-slate-300"
                    >
                      All
                    </Button>
                  )}
                  {bulkMoveSelected.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkMoveSelected([])}
                      data-testid="bulkmove-clear-btn"
                      className="border-slate-600 text-slate-300"
                    >
                      Clear ({bulkMoveSelected.length})
                    </Button>
                  )}
                </div>
              </div>

              {voiceMembers.length === 0 ? (
                <div className="text-center py-8 text-slate-400 bg-slate-900/50 rounded-lg border border-slate-700">
                  <WifiOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No members currently in voice channels</p>
                  <p className="text-xs mt-1">Bulk move only works on members who are already in a voice channel.</p>
                </div>
              ) : (
                <div className="grid gap-2 max-h-64 sm:max-h-80 overflow-y-auto" data-testid="bulkmove-member-list">
                  {voiceMembers
                    .filter(m => String(m.channel_id) !== String(bulkMoveTarget))
                    .map((member) => {
                      const checked = bulkMoveSelected.includes(member.discord_id);
                      return (
                        <button
                          key={member.discord_id}
                          onClick={() => toggleBulkMoveMember(member.discord_id)}
                          data-testid={`bulkmove-member-${member.discord_id}`}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                            checked
                              ? 'bg-emerald-600/30 border-emerald-500 ring-2 ring-emerald-500/50'
                              : 'bg-slate-900/50 border-slate-700 hover:bg-slate-800'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            checked ? 'bg-emerald-600 border-emerald-600' : 'border-slate-600'
                          }`}>
                            {checked && <Send className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{member.display_name}</p>
                            <p className="text-xs text-slate-400 flex items-center gap-1 truncate">
                              <Hash className="w-3 h-3 flex-shrink-0" /> {member.channel_name}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Submit */}
            <Button
              onClick={handleBulkMove}
              disabled={bulkMoveSelected.length === 0 || !bulkMoveTarget || isExecuting}
              data-testid="bulkmove-submit-btn"
              className="w-full h-12 sm:h-14 text-base sm:text-lg font-bold bg-gradient-to-r from-emerald-600 to-emerald-800 hover:from-emerald-700 hover:to-emerald-900"
            >
              {isExecuting ? (
                <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Moving...</>
              ) : (
                <><Send className="w-5 h-5 mr-2" /> Move {bulkMoveSelected.length} Member{bulkMoveSelected.length !== 1 ? 's' : ''}</>
              )}
            </Button>

            <p className="text-xs text-slate-500 mt-4 text-center">
              Members already in the target channel are filtered out automatically. Members who left voice are skipped.
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 mb-20 md:mb-0 text-center text-slate-500 text-sm">
          <p>⚡ Auto-refreshes every 5 seconds</p>
        </div>
      </div>

      {/* Floating Quick Actions Bar — phone only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-slate-900/95 backdrop-blur border-t border-slate-700 shadow-2xl">
        <button
          onClick={() => setQuickSheetOpen(!quickSheetOpen)}
          data-testid="quick-actions-toggle"
          className="w-full flex items-center justify-between px-4 py-3"
        >
          <div className="flex items-center gap-2 text-white">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center">
              <Bolt className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold">Quick Actions</p>
              <p className="text-[11px] text-slate-400">{voiceMembers.length} in voice</p>
            </div>
          </div>
          {quickSheetOpen ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          )}
        </button>

        {/* Member list — slides up */}
        {quickSheetOpen && (
          <div
            data-testid="quick-actions-sheet"
            className="border-t border-slate-700 max-h-[60vh] overflow-y-auto"
          >
            {voiceMembers.length === 0 ? (
              <div className="text-center py-6 text-slate-400 text-sm">
                <WifiOff className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No members in voice right now</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {voiceMembers.map((m) => (
                  <div key={m.discord_id} data-testid={`quick-row-${m.discord_id}`} className="px-4 py-3">
                    <button
                      onClick={() =>
                        setQuickActionMember(
                          quickActionMember?.discord_id === m.discord_id ? null : m
                        )
                      }
                      data-testid={`quick-pick-${m.discord_id}`}
                      className="w-full flex items-center gap-3 text-left"
                    >
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{m.display_name}</p>
                        <p className="text-[11px] text-slate-400 flex items-center gap-1 truncate">
                          <Hash className="w-3 h-3" /> {m.channel_name}
                        </p>
                      </div>
                      {quickActionMember?.discord_id === m.discord_id ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      )}
                    </button>

                    {/* Action buttons - only show for picked member */}
                    {quickActionMember?.discord_id === m.discord_id && (
                      <div className="grid grid-cols-5 gap-1.5 mt-3">
                        <button
                          onClick={() => runQuickAction(m, "disconnect")}
                          disabled={isExecuting}
                          data-testid={`quick-disconnect-${m.discord_id}`}
                          className="flex flex-col items-center gap-1 py-2 rounded-lg bg-red-900/40 border border-red-800 text-red-200 active:bg-red-900/70 disabled:opacity-50"
                        >
                          <WifiOff className="w-4 h-4" />
                          <span className="text-[10px]">Kick</span>
                        </button>
                        <button
                          onClick={() => runQuickAction(m, "random")}
                          disabled={isExecuting}
                          data-testid={`quick-random-${m.discord_id}`}
                          className="flex flex-col items-center gap-1 py-2 rounded-lg bg-purple-900/40 border border-purple-800 text-purple-200 active:bg-purple-900/70 disabled:opacity-50"
                        >
                          <Shuffle className="w-4 h-4" />
                          <span className="text-[10px]">Random</span>
                        </button>
                        <button
                          onClick={() => runQuickAction(m, "deafen")}
                          disabled={isExecuting}
                          data-testid={`quick-deafen-${m.discord_id}`}
                          className="flex flex-col items-center gap-1 py-2 rounded-lg bg-amber-900/40 border border-amber-800 text-amber-200 active:bg-amber-900/70 disabled:opacity-50"
                        >
                          <Headphones className="w-4 h-4" />
                          <span className="text-[10px]">Cone</span>
                        </button>
                        <button
                          onClick={() => runQuickAction(m, "boomerang")}
                          disabled={isExecuting}
                          data-testid={`quick-boomerang-${m.discord_id}`}
                          className="flex flex-col items-center gap-1 py-2 rounded-lg bg-cyan-900/40 border border-cyan-800 text-cyan-200 active:bg-cyan-900/70 disabled:opacity-50"
                        >
                          <Repeat className="w-4 h-4" />
                          <span className="text-[10px]">Boom</span>
                        </button>
                        <button
                          onClick={() => runQuickAction(m, "lock")}
                          disabled={isExecuting}
                          data-testid={`quick-lock-${m.discord_id}`}
                          className="flex flex-col items-center gap-1 py-2 rounded-lg bg-rose-900/40 border border-rose-800 text-rose-200 active:bg-rose-900/70 disabled:opacity-50"
                        >
                          <Lock className="w-4 h-4" />
                          <span className="text-[10px]">Lock</span>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
